/**
 * `wayai relationships …` — typed edges between records. Top-level: WayAI owns
 * no `relationships` concept.
 */

import { Command } from 'commander';
import { expected } from '../../lib/expected.js';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import {
  foreignSegment,
  historyQuery,
  outputFormat,
  parseData,
  pathSegment,
  requireBase,
} from '../helpers.js';
import { confirm } from '../../lib/utils.js';

/**
 * A relationship's `<id>` argument, encoded for the path it lands in. With
 * `--rel-type` the argument is the relationship's EXTERNAL key, minted
 * upstream, so the slug grammar would reject legitimate values; without it, it
 * is an id this platform issued.
 */
function relationshipKey(id: string, relType: string | undefined): string {
  return relType ? foreignSegment(id, 'external id') : pathSegment(id, 'relationship id');
}

/**
 * One endpoint's write-body fields from the two addressing flags:
 * `--<role> <record_type/id>` (internal id, verbatim — may dangle) XOR
 * `--<role>-external <record_type/external_id>` (resolved server-side; the
 * record must exist). Mirrors the server's per-endpoint contract.
 */
function endpointBodyFields(
  role: 'source' | 'target',
  byId: string | undefined,
  byExternal: string | undefined,
  externalSource: string | undefined,
): Record<string, unknown> {
  if ((byId ? 1 : 0) + (byExternal ? 1 : 0) !== 1) {
    throw expected(`Provide exactly one of --${role} or --${role}-external.`);
  }
  const ref = (byId ?? byExternal)!;
  // Exactly two segments: extra slashes must fail loud, not silently truncate —
  // 'invoices/inv/001' would otherwise send external_id 'inv', linking a
  // different (possibly existing) record.
  const segments = ref.split('/');
  const [recordType, key] = segments;
  if (segments.length !== 2 || !recordType || !key) {
    throw expected(
      `--${role}${byId ? '' : '-external'} must be <record_type/${byId ? 'id' : 'external_id'}>, got '${ref}'.`,
    );
  }
  if (externalSource && !byExternal) {
    throw expected(`--${role}-external-source requires --${role}-external.`);
  }
  const fields: Record<string, unknown> = { [`${role}_record_type`]: recordType };
  if (byId) {
    fields[`${role}_id`] = key;
  } else {
    fields[`${role}_external_id`] = key;
    if (externalSource) fields[`${role}_external_source`] = externalSource;
  }
  return fields;
}

/**
 * By-key addressing query params: `--rel-type` scopes an external_id lookup,
 * because uniqueness is per rel_type.
 */
function byKeyQuery(relType?: string, externalSource?: string): string {
  const qs = new URLSearchParams();
  if (relType) qs.set('rel_type', relType);
  if (externalSource) qs.set('external_source', externalSource);
  const q = qs.toString();
  return q ? `?${q}` : '';
}

export function buildRelationshipsCommand(): Command {
  const relationships = new Command('relationships').description(
    'Manage relationships between records',
  );

  relationships
    .command('upsert')
    .description('Create or update a relationship')
    .option('--source <record_type/id>', 'Source record by internal id')
    .option(
      '--source-external <record_type/external_id>',
      'Source record by external_id (the record must exist)',
    )
    .option('--source-external-source <name>', 'Source system scoping --source-external')
    .option('--target <record_type/id>', 'Target record by internal id')
    .option(
      '--target-external <record_type/external_id>',
      'Target record by external_id (the record must exist)',
    )
    .option('--target-external-source <name>', 'Source system scoping --target-external')
    .requiredOption('--type <type>', 'Relationship type')
    .option('--id <id>', 'Relationship ID')
    .option(
      '--external-id <key>',
      "The relationship's own external key: a retried upsert with the same key updates the first row instead of duplicating it",
    )
    .option('--external-source <name>', "Source system scoping the relationship's --external-id")
    .option('--data <json>', 'Relationship metadata (inline JSON or @filename)')
    .action(async function (
      this: Command,
      opts: {
        source?: string;
        sourceExternal?: string;
        sourceExternalSource?: string;
        target?: string;
        targetExternal?: string;
        targetExternalSource?: string;
        type: string;
        id?: string;
        externalId?: string;
        externalSource?: string;
        data?: string;
      },
    ) {
      const body: Record<string, unknown> = {
        rel_type: opts.type,
        ...endpointBodyFields('source', opts.source, opts.sourceExternal, opts.sourceExternalSource),
        ...endpointBodyFields('target', opts.target, opts.targetExternal, opts.targetExternalSource),
      };
      if (opts.id) body.id = opts.id;
      if (opts.externalId) body.external_id = opts.externalId;
      if (opts.externalSource) body.external_source = opts.externalSource;
      if (opts.data) body.data = parseData(opts.data);

      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const path = opts.id
        ? `/v1/${base}/relationships/${pathSegment(opts.id, '--id')}`
        : `/v1/${base}/relationships`;
      printOutput(await client.request('PUT', path, body), outputFormat(this));
    });

  relationships
    .command('get <id>')
    .description(
      'Get a relationship by ID (or by its external key: pass the external_id with --rel-type)',
    )
    .option(
      '--rel-type <type>',
      'Treat <id> as the relationship external_id, scoped to this relationship type',
    )
    .option('--external-source <name>', 'Source system scoping the external_id (with --rel-type)')
    .action(async function (
      this: Command,
      id: string,
      opts: { relType?: string; externalSource?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request(
          'GET',
          `/v1/${base}/relationships/${relationshipKey(id, opts.relType)}${byKeyQuery(opts.relType, opts.externalSource)}`,
        ),
        outputFormat(this),
      );
    });

  relationships
    .command('delete <id>')
    .description(
      'Delete a relationship by ID (or by its external key: pass the external_id with --rel-type)',
    )
    .option(
      '--rel-type <type>',
      'Treat <id> as the relationship external_id, scoped to this relationship type',
    )
    .option('--external-source <name>', 'Source system scoping the external_id (with --rel-type)')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (
      this: Command,
      id: string,
      opts: { relType?: string; externalSource?: string; yes?: boolean },
    ) {
      if (!opts.yes && !(await confirm(`Delete relationship ${id}? This cannot be undone.`))) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request(
        'DELETE',
        `/v1/${base}/relationships/${relationshipKey(id, opts.relType)}${byKeyQuery(opts.relType, opts.externalSource)}`,
      );
      console.log('Deleted');
    });

  relationships
    .command('history <id>')
    .description(
      'View the change history (audit log) for a relationship — every version, who changed it, and when',
    )
    .option('--limit <n>', 'Max results')
    .option('--offset <n>', 'Pagination offset')
    .option('--diff', 'Include field-level diffs between versions')
    .action(async function (
      this: Command,
      id: string,
      opts: { limit?: string; offset?: string; diff?: boolean },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('GET', `/v1/${base}/audit/relationships/${pathSegment(id, 'relationship id')}${historyQuery(opts)}`),
        outputFormat(this),
      );
    });

  return relationships;
}
